/**
 * Hook that detects SEG series on a fresh submission (no CBOR history)
 * and seeds the annotation store with pre-existing mask annotations.
 *
 * Flow: fetch SEG NIfTI → load into NVImage → split by label → RLE-encode
 * → return MaskAnnotationState[] for the caller to apply.
 */

import { NVImage } from "@niivue/niivue"
import { encodeRLE } from "@niivue/niivue/drawing"
import type { MaskAnnotationState, MaskNiftiEntry } from "@shared/lib/history-types"
import { generateId } from "@shared/lib/id"
import { useCallback, useRef } from "react"

interface SeriesMetadataItem {
  key: string
  value: string
}

interface SegSeriesInfo {
  seriesId: string
  segmentationLabels: Record<string, string>
  sourceSeriesIdentifier: string | null
}

interface SeedMaskLoaderParams {
  caseId: string
  series: Array<{
    id: string
    series_identifier: string
    image_type: string | null
  }>
  maskModuleId: string
  maskLabels: Array<{ key: string; value?: number }>
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
}

export function useSeedMaskLoader() {
  const loadingRef = useRef(false)

  const loadSeedMasks = useCallback(
    async (params: SeedMaskLoaderParams): Promise<{ masks: MaskAnnotationState[]; niftis: MaskNiftiEntry[] }> => {
      if (loadingRef.current) return { masks: [], niftis: [] }
      loadingRef.current = true

      try {
        const { caseId, series, maskModuleId, maskLabels, getAuthHeaders } = params

        const empty = { masks: [], niftis: [] }
        const segSeries = series.filter((s) => s.image_type === "SEG")
        if (segSeries.length === 0) return empty

        const headers = await getAuthHeaders()

        // Fetch metadata for all SEG series in parallel
        const metaResults = await Promise.all(
          segSeries.map(async (seg) => {
            const res = await fetch(`/api/cases/${caseId}/series/${seg.id}/metadata`, { headers })
            if (!res.ok) return null
            const data = (await res.json()) as { data: SeriesMetadataItem[] }
            return { seriesId: seg.id, metadata: data.data }
          })
        )

        const segInfos: SegSeriesInfo[] = []
        for (const result of metaResults) {
          if (!result) continue
          const labelsEntry = result.metadata.find((m) => m.key === "segmentation_labels")
          if (!labelsEntry) continue

          let segLabels: Record<string, string>
          try {
            segLabels = JSON.parse(labelsEntry.value)
          } catch {
            continue
          }

          const sourceEntry = result.metadata.find((m) => m.key === "source_series_identifier")

          segInfos.push({
            seriesId: result.seriesId,
            segmentationLabels: segLabels,
            sourceSeriesIdentifier: sourceEntry?.value ?? null
          })
        }

        if (segInfos.length === 0) return empty

        // Build label key → index map from module template
        const labelKeyToIdx = new Map<string, number>()
        for (let i = 0; i < maskLabels.length; i++) {
          labelKeyToIdx.set(maskLabels[i]!.key, i)
        }

        // Fetch all SEG NIfTI files in parallel
        const fileResults = await Promise.all(
          segInfos.map(async (segInfo) => {
            // Resolve source imaging series
            let targetSeriesId: string | null = null
            if (segInfo.sourceSeriesIdentifier) {
              const match = series.find(
                (s) => s.series_identifier === segInfo.sourceSeriesIdentifier && s.image_type !== "SEG"
              )
              targetSeriesId = match?.id ?? null
            }
            if (!targetSeriesId) return null

            const fileRes = await fetch(`/api/cases/${caseId}/series/${segInfo.seriesId}/file`, { headers })
            if (!fileRes.ok) return null

            const blob = await fileRes.blob()
            return { segInfo, targetSeriesId, blob }
          })
        )

        const newMasks: MaskAnnotationState[] = []
        const newNiftis: MaskNiftiEntry[] = []

        for (const result of fileResults) {
          if (!result) continue
          const { segInfo, targetSeriesId, blob } = result
          const blobUrl = URL.createObjectURL(blob)

          try {
            const image = await NVImage.loadFromUrl({
              url: blobUrl,
              name: `seed-${segInfo.seriesId}.nii.gz`
            })

            const voxels = image.img
            if (!voxels || voxels.length === 0) continue

            // Seed masks are derived from a single multi-label SEG volume where
            // each voxel maps to exactly one voxelValue, so the generated label
            // masks are non-overlapping among themselves by construction. They
            // are intentionally exempt from the mask module's `disallow_overlap`
            // clip (which only applies to interactive brush/SAM commits).
            for (const [voxelValueStr, labelName] of Object.entries(segInfo.segmentationLabels)) {
              const voxelValue = parseInt(voxelValueStr, 10)
              if (voxelValue === 0) continue

              const labelIdx = labelKeyToIdx.get(labelName)
              if (labelIdx === undefined) continue
              const labelDef = maskLabels[labelIdx]!

              // Single pass: create binary mask and check for content
              const binaryMask = new Uint8Array(voxels.length)
              let hasContent = false
              for (let i = 0; i < voxels.length; i++) {
                if (voxels[i] === voxelValue) {
                  binaryMask[i] = 1
                  hasContent = true
                }
              }
              if (!hasContent) continue

              const annotationId = generateId()

              newMasks.push({
                id: annotationId,
                seriesId: targetSeriesId,
                projectModuleId: maskModuleId,
                labelName,
                labelValue: labelDef.value ?? labelIdx + 1,
                instanceOrdinal: 1,
                rle: encodeRLE(binaryMask)
              })

              // Generate compressed NIfTI using the SEG image's header geometry
              try {
                const nifti = await image.saveToUint8Array("mask.nii.gz", binaryMask)
                newNiftis.push({ annotationId, nifti })
              } catch {
                // NIfTI generation is best-effort; RLE is the primary data
              }
            }
          } finally {
            URL.revokeObjectURL(blobUrl)
          }
        }

        return { masks: newMasks, niftis: newNiftis }
      } finally {
        loadingRef.current = false
      }
    },
    []
  )

  return { loadSeedMasks }
}
